import { useCallback, useMemo } from 'react'
import { useAppSelector } from 'store/useStoreHook'
import { PaymentSettingContract } from 'models'
import Decimal from 'decimal.js'
import { PaymentPayload } from './usePaymentDialog'

export interface IPaymentDetailProps {
    selectedPaymentType: PaymentSettingContract | undefined
    payment: PaymentPayload | undefined
    expectAmount: Decimal
}

export function usePaymentCalculation({
    selectedPaymentType: paymentSetting,
    payment,
    expectAmount,
}: IPaymentDetailProps) {
    // Ensure that we have operation currency label on this state
    const currencyCode = useAppSelector(
        (state) => state.operation.data.currencyCode,
    )

    const isRounding = useMemo(() => {
        return paymentSetting?.isCashRoundingEnabled ?? false
    }, [paymentSetting])

    const cashRoundingAdjustAmount = useMemo(() => {
        if (!isRounding) return new Decimal(0)
        return expectAmount
            .toNearest(paymentSetting?.roundOffTotalsToNearest ?? 0)
            .minus(expectAmount)
    }, [expectAmount, isRounding, paymentSetting])

    const balanceDueAmountCalculation = useCallback(
        (expectAmount: Decimal) => {
            if (isRounding)
                return expectAmount.toNearest(
                    paymentSetting?.roundOffTotalsToNearest ?? 0,
                )
            return expectAmount
        },
        [isRounding, paymentSetting],
    )
    const balanceDueAmount = useMemo(() => {
        return balanceDueAmountCalculation(expectAmount)
    }, [balanceDueAmountCalculation, expectAmount])

    const tenderedAmount = useMemo(() => {
        return new Decimal(payment?.tenderedAmount ?? 0)
    }, [payment])

    const collectValue = useMemo(() => {
        if (tenderedAmount.greaterThan(balanceDueAmount))
            return balanceDueAmount
        return tenderedAmount
    }, [tenderedAmount, balanceDueAmount])

    const changeValue = useMemo(() => {
        return tenderedAmount.minus(balanceDueAmount)
    }, [tenderedAmount, balanceDueAmount])
    const isMoneyCollect = useMemo(
        () => collectValue.toNumber() === balanceDueAmount.toNumber(),
        [collectValue, balanceDueAmount],
    )
    return {
        currencyCode,
        balanceDueAmount,
        balanceDueAmountCalculation,
        collectValue,
        changeValue,
        cashRoundingAdjustAmount,
        isMoneyCollect,
    }
}
